import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  EmptyState,
  GlassCard,
  InfoBanner,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { PayRunSummaryCard } from './pay_run_summary_card';
import { resolve_approver_name } from './pay_run_complete_view_logic';
import { PayRunCompleteView } from './pay_run_complete_view';
import { PayRunStepRail } from './pay_run_step_rail';
import { StartPayRunPanel } from './start_pay_run_panel';
import {
  ApprovalStep,
  ExceptionsStep,
  FinalReviewStep,
  PeriodScopeStep,
  PostRunStep,
  ReviewPayStep,
  StatutoryStep,
} from './wizard_steps';
import {
  FIRST_STEP_NUMBER,
  can_advance,
  can_go_back,
  can_sign_off,
  next_step,
  pay_run_view_key,
  pay_run_view_mode,
  previous_step,
  step_at,
} from './pay_run_calculations';
import type { WizardStep } from './pay_run_calculations';
import { WIZARD_STEP_KEY } from '../../lib/label_keys';
import { isInProgressPayRun, usePayRuns } from '../../data/use_pay_runs';
import { usePayRunAudit } from '../../data/use_pay_run_audit';
import { useEmployees } from '../../data/use_employees';
import { count_active_contractors } from '../../lib/worker_types';
import type {
  BackPayWarning,
  PayRunMutationFailure,
  SkippedForeignCurrencyEmployee,
} from '../../data/use_pay_runs';
import { format_month_year, format_number } from '../../lib/format';
import type { PayRun } from '../../data/types';

/**
 * Run payroll: the seven-step wizard while the current run is being
 * prepared, and the completion view once it is approved (with the wizard
 * still reachable through "View pay run details").
 */
export function RunPayrollScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    state: pay_runs_state,
    retry,
    acknowledge_exception,
    sign_off,
    start_pay_run,
    finalise_pay_run,
    set_line_deltas,
    is_read_only_grant,
    post_actions,
    exception_ack_available,
  } = usePayRuns();
  // Contractors are never included in a pay run (the API excludes them at
  // creation). Say so only when the entity actually has an ACTIVE one -- a
  // contractor whose engagement was terminated is not "left out".
  const { employees } = useEmployees();
  const contractor_count = count_active_contractors(employees);
  const [step, set_step] = useState<number>(FIRST_STEP_NUMBER);
  // The employer clicked "Start new pay run" on a finished latest run.
  const [starting_new_run, set_starting_new_run] = useState(false);
  // The employer opened the full wizard from a finished run's completion view.
  const [show_details, set_show_details] = useState(false);
  // Focus the completion heading when it replaces the wizard after an
  // interaction (sign-off, "Back to summary") -- never on a plain page load.
  const [focus_completion, set_focus_completion] = useState(false);
  const ready_run =
    pay_runs_state.status === 'ready' ? pay_runs_state.current_run : null;
  // A new current run, or the same run reloaded in a new status (sign-off
  // succeeded), starts over: first wizard step, details closed. Without this
  // a successful sign-off left the wizard frozen on the Approval step. Uses
  // React's "adjust state while rendering" idiom, so no stale frame paints.
  // A loading/error read keeps the previous key: the reset happens once the
  // reloaded run arrives.
  const run_view_key = ready_run === null ? null : pay_run_view_key(ready_run);
  const [seen_run_view_key, set_seen_run_view_key] = useState(run_view_key);
  if (run_view_key !== null && run_view_key !== seen_run_view_key) {
    // A first load (no previous key) is not a transition worth announcing.
    set_focus_completion(seen_run_view_key !== null);
    set_seen_run_view_key(run_view_key);
    set_step(FIRST_STEP_NUMBER);
    set_show_details(false);
  }
  // Only a finished run needs its approver's name (from the audit feed); a
  // `null` id makes the hook do nothing at all.
  const finished_run =
    ready_run !== null && pay_run_view_mode(ready_run) === 'complete'
      ? ready_run
      : null;
  const { approver_display_name } = usePayRunAudit(
    finished_run?.id ?? null,
    finished_run?.created_at ?? null,
  );
  const [skipped_foreign, set_skipped_foreign] = useState<
    readonly SkippedForeignCurrencyEmployee[]
  >([]);
  const [back_pay_warnings, set_back_pay_warnings] = useState<
    readonly BackPayWarning[]
  >([]);
  const [is_signing_off, set_is_signing_off] = useState(false);
  const [sign_off_error, set_sign_off_error] = useState<
    PayRunMutationFailure | undefined
  >(undefined);
  // approvePayRun's 'has_contractor' refusal is only as fresh as the data it
  // was raised against. Once the pay runs refetch (the read state leaves
  // 'ready'), drop it: the fresh preview's `blocked_contractor_count` then
  // decides -- > 0 keeps the blocked banner up on its own, 0 means the
  // draft is approvable again and the stale refusal must not linger.
  useEffect(() => {
    if (pay_runs_state.status === 'ready') return;
    set_sign_off_error((previous) =>
      previous === 'has_contractor' ? undefined : previous,
    );
  }, [pay_runs_state.status]);
  const [is_finalising, set_is_finalising] = useState(false);
  const [finalise_error, set_finalise_error] = useState<
    PayRunMutationFailure | undefined
  >(undefined);

  // `no_entity` is unreachable in practice -- see `use_pay_runs.ts`'s module
  // docblock; `main.tsx`'s `AuthGate` already gates the whole app on it.
  if (
    pay_runs_state.status === 'loading' ||
    pay_runs_state.status === 'no_entity'
  ) {
    return (
      <GlassCard variant="accent" className="rounded-2xl p-5">
        <span className="sr-only">{t('pay_run.loading')}</span>
        <div className="nv-skel" style={{ height: 44, margin: 8 }} />
        <div className="nv-skel" style={{ height: 44, margin: 8 }} />
        <div className="nv-skel" style={{ height: 44, margin: 8 }} />
      </GlassCard>
    );
  }

  if (pay_runs_state.status === 'entitlement_required') {
    return (
      <EmptyState
        title={t('pay_run.entitlement_required_title')}
        description={t('pay_run.entitlement_required_description')}
      />
    );
  }

  if (pay_runs_state.status === 'error') {
    return (
      <EmptyState
        title={t('pay_run.load_error_title')}
        description={t('pay_run.load_error_description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('pay_run.retry')}
          </Button>
        }
      />
    );
  }

  const { current_run } = pay_runs_state;
  // An approved latest run is effectively final on the real backend (it is
  // never moved past `approved`), so it shows the completion view -- with
  // the post-run next steps (payslips, filings) and a "start next pay run"
  // CTA -- instead of the wizard. Only a draft blocks starting a new run. When every run is voided
  // there is nothing to show, so the picker appears straight away.
  const has_run_in_progress =
    current_run !== null && isInProgressPayRun(current_run);
  if (
    current_run === null ||
    current_run.status === 'voided' ||
    (starting_new_run && !has_run_in_progress)
  ) {
    return (
      <StartPayRunPanel
        pay_runs={pay_runs_state.pay_runs}
        start_pay_run={start_pay_run}
        is_read_only_grant={is_read_only_grant}
        on_started={(skipped, warnings) => {
          set_skipped_foreign(skipped);
          set_back_pay_warnings(warnings);
          set_starting_new_run(false);
          set_step(FIRST_STEP_NUMBER);
        }}
        {...(current_run !== null && current_run.status !== 'voided'
          ? {
              // Only reachable via "Start new pay run" on a finished run.
              back_to_period: current_run.period,
              on_back: () => {
                set_starting_new_run(false);
              },
            }
          : {})}
      />
    );
  }

  const sign_off_available = sign_off !== undefined;
  const finalise_available = finalise_pay_run !== undefined;
  const can_sign = can_sign_off(current_run);
  const is_complete = pay_run_view_mode(current_run) === 'complete';
  // Resolved once so the completion view and the Approval step can never
  // disagree: the audit feed's display name first, then the run's own.
  const approver_name = resolve_approver_name(
    approver_display_name,
    current_run,
  );
  const show_completion = is_complete && !show_details;

  return (
    <>
      <ScreenHeader
        title={t('pay_run.title', {
          period: format_month_year(current_run.period, locale),
        })}
        description={t('pay_run.subtitle', {
          count: current_run.employee_count,
        })}
        actions={
          // The completion view carries its own "start next" CTA.
          !show_completion && !has_run_in_progress && start_pay_run ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                set_starting_new_run(true);
              }}
            >
              {t('pay_run.start_new_pay_run')}
            </Button>
          ) : undefined
        }
      />

      {skipped_foreign.length > 0 && (
        <Alert variant="warning" className="mb-4">
          {t('pay_run.skipped_foreign_currency', {
            count: skipped_foreign.length,
            names: skipped_foreign.map((e) => e.full_name).join(', '),
          })}
        </Alert>
      )}
      {contractor_count > 0 && (
        <div className="mb-4" data-testid="pay-run-contractors-note">
          <InfoBanner>
            {t('pay_run.contractors_excluded_note', {
              count: contractor_count,
            })}
          </InfoBanner>
        </div>
      )}

      {back_pay_warnings.length > 0 && (
        <Alert
          variant="warning"
          title={t('pay_run.back_pay_warning_title')}
          className="mb-4"
        >
          {back_pay_warnings.some((w) => w.reason === 'no_snapshot') && (
            <p>{t('pay_run.back_pay_manual_warning')}</p>
          )}
          {back_pay_warnings.some((w) => w.reason === 'currency_changed') && (
            <p>{t('pay_run.back_pay_currency_warning')}</p>
          )}
        </Alert>
      )}

      {show_completion ? (
        <PayRunCompleteView
          run={current_run}
          pay_runs={pay_runs_state.pay_runs}
          approver_name={approver_name}
          post_actions={post_actions}
          focus_heading={focus_completion}
          start_available={start_pay_run !== undefined}
          on_start_next={() => {
            set_starting_new_run(true);
          }}
          on_view_details={() => {
            set_focus_completion(false);
            set_show_details(true);
          }}
        />
      ) : (
        <>
          {is_complete && (
            <div className="mb-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  set_focus_completion(true);
                  set_show_details(false);
                }}
              >
                {t('pay_run.back_to_summary')}
              </Button>
            </div>
          )}
          <PayRunStepRail step={step} on_select={set_step} />

          <div className="grid gap-4 items-start grid-cols-1 lg:grid-cols-[1fr_300px]">
            <GlassCard className="rounded-2xl p-5">
              <h2 className="font-bold mb-3">
                {t('pay_run.step_heading', {
                  number: format_number(step, locale),
                  label: t(WIZARD_STEP_KEY[step_at(step)]),
                })}
              </h2>

              <StepPanel
                step={step_at(step)}
                run={current_run}
                on_acknowledge={(exception_id) => {
                  acknowledge_exception(current_run.id, exception_id);
                }}
                exception_ack_available={exception_ack_available}
                can_sign={can_sign}
                sign_off_available={sign_off_available}
                is_signing_off={is_signing_off}
                sign_off_error={sign_off_error}
                on_sign_off={() => {
                  if (!sign_off) return;
                  set_is_signing_off(true);
                  set_sign_off_error(undefined);
                  // `.finally()` resets `is_signing_off` regardless of outcome
                  // (adversarial review finding 4) -- same reasoning as
                  // `start_pay_run`'s handler above.
                  void sign_off(current_run.id)
                    .then((result) => {
                      if (result.outcome === 'failure') {
                        set_sign_off_error(result.failure);
                        // Refetch so the blocked banner reflects the live
                        // contractor count (see the effect above).
                        if (result.failure === 'has_contractor') retry();
                      }
                    })
                    .finally(() => {
                      set_is_signing_off(false);
                    });
                }}
                finalise_available={finalise_available}
                is_finalising={is_finalising}
                finalise_error={finalise_error}
                on_finalise={() => {
                  if (!finalise_pay_run) return;
                  set_is_finalising(true);
                  set_finalise_error(undefined);
                  // Same `.finally()` reset pattern as `sign_off` above: keep
                  // the disabled state coherent even if the mutation contract
                  // is violated in a future refactor.
                  void finalise_pay_run(current_run.id)
                    .then((result) => {
                      if (result.outcome === 'failure') {
                        set_finalise_error(result.failure);
                      }
                    })
                    .finally(() => {
                      set_is_finalising(false);
                    });
                }}
                on_edit_line={set_line_deltas}
                post_actions={post_actions}
                approved_by_name={approver_name}
              />

              <div className="flex justify-between mt-5 pt-3.5 border-t border-[color:var(--line)]">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!can_go_back(step)}
                  onClick={() => {
                    set_step(previous_step(step));
                  }}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!can_advance(current_run, step)}
                  onClick={() => {
                    set_step(next_step(current_run, step));
                  }}
                >
                  {t('common.continue')}
                </Button>
              </div>
            </GlassCard>

            <PayRunSummaryCard run={current_run} />
          </div>
        </>
      )}
    </>
  );
}

function StepPanel({
  step,
  run,
  on_acknowledge,
  exception_ack_available,
  can_sign,
  sign_off_available,
  is_signing_off,
  sign_off_error,
  on_sign_off,
  finalise_available,
  is_finalising,
  finalise_error,
  on_finalise,
  on_edit_line,
  post_actions,
  approved_by_name,
}: {
  step: WizardStep;
  run: PayRun;
  on_acknowledge: (exception_id: string) => void;
  exception_ack_available: boolean;
  can_sign: boolean;
  sign_off_available: boolean;
  is_signing_off: boolean;
  sign_off_error: PayRunMutationFailure | undefined;
  on_sign_off: () => void;
  finalise_available: boolean;
  is_finalising: boolean;
  finalise_error: PayRunMutationFailure | undefined;
  on_finalise: () => void;
  on_edit_line: ReturnType<typeof usePayRuns>['set_line_deltas'];
  post_actions: ReturnType<typeof usePayRuns>['post_actions'];
  approved_by_name: string | undefined;
}): React.JSX.Element {
  switch (step) {
    case 'period_scope':
      return <PeriodScopeStep run={run} />;
    case 'review_pay':
      return <ReviewPayStep run={run} on_edit_line={on_edit_line} />;
    case 'statutory_summary':
      return <StatutoryStep run={run} />;
    case 'exceptions':
      return (
        <ExceptionsStep
          run={run}
          on_acknowledge={on_acknowledge}
          ack_available={exception_ack_available}
        />
      );
    case 'approval':
      return (
        <ApprovalStep
          run={run}
          can_sign={can_sign}
          sign_off_available={sign_off_available}
          is_signing_off={is_signing_off}
          sign_off_error={sign_off_error}
          on_sign_off={on_sign_off}
          {...(approved_by_name === undefined ? {} : { approved_by_name })}
        />
      );
    case 'final_review':
      return (
        <FinalReviewStep
          run={run}
          finalise_available={finalise_available}
          is_finalising={is_finalising}
          finalise_error={finalise_error}
          on_finalise={on_finalise}
        />
      );
    case 'post_run':
      return <PostRunStep run={run} post_actions={post_actions} />;
  }
}
